import React, { useEffect } from 'react';
import Layout from '../layout/Layout';
import { usePageContent } from '../../context/ContentContext';

const BulletList = ({ items }) => (
    <ul className="font-poppins font-normal text-body text-gray-700 space-y-4">
        {items.map((item, index) => (
            <li key={index} className="flex items-start">
                <span className="text-primary-red mr-3 mt-1">•</span>
                <span>{item}</span>
            </li>
        ))}
    </ul>
);

const EnvironmentalPolicyPage = () => {
    const { t, g } = usePageContent('environmentalPolicy');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <Layout>
            {/* Hero Section */}
            <section className="relative h-[70vh] overflow-hidden">
                <img
                    src={t('hero.image')}
                    alt={t('hero.title')}
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60 flex items-center justify-center">
                    <h1 className="font-poppins font-light text-h1 text-white text-center px-4">
                        {t('hero.title')}
                    </h1>
                </div>
            </section>

            {/* Introduction */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="font-poppins font-light text-h2 mb-8 text-primary-dark-red">
                            {t('commitment.title')}
                        </h2>
                        <p className="font-poppins font-normal text-body mb-6">
                            {t('commitment.body')}
                        </p>
                    </div>
                </div>
            </section>

            {/* Freight and Shipping */}
            <section className="py-20 bg-background-cool-blue">
                <div className="container mx-auto px-4">
                    <h2 className="font-poppins font-light text-h2 text-center mb-12 text-primary-dark-red">
                        {t('freight.title')}
                    </h2>

                    <div className="max-w-4xl mx-auto">
                        <div className="bg-white p-8 rounded-lg shadow-sm">
                            <BulletList items={t('freight.items')} />
                        </div>
                    </div>
                </div>
            </section>

            {/* Waste Reduction */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="font-poppins font-light text-h2 text-center mb-12 text-primary-dark-red">
                        {t('waste.title')}
                    </h2>

                    <div className="max-w-4xl mx-auto">
                        <div className="bg-accent-powder-green p-8 rounded-lg">
                            <BulletList items={t('waste.items')} />
                        </div>
                    </div>
                </div>
            </section>

            {/* Sustainable Materials */}
            <section className="py-20 bg-background-cool-blue">
                <div className="container mx-auto px-4">
                    <h2 className="font-poppins font-light text-h2 text-center mb-12 text-primary-dark-red">
                        {t('materials.title')}
                    </h2>

                    <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8">
                        {t('materials.cards').map((card, index) => (
                            <div key={index} className="bg-white p-6 rounded-lg text-center">
                                <div className="flex items-center justify-center mb-4 h-24">
                                    <img
                                        src={card.image}
                                        alt={card.title}
                                        className="h-full w-auto object-contain"
                                    />
                                </div>
                                <h3 className="font-poppins font-semibold text-h3 mb-4 text-primary-red">{card.title}</h3>
                                <p className="font-poppins font-normal text-sub text-gray-700">
                                    {card.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Energy and Resources */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="font-poppins font-light text-h2 text-center mb-12 text-primary-dark-red">
                        {t('energy.title')}
                    </h2>

                    <div className="max-w-4xl mx-auto">
                        <div className="grid md:grid-cols-2 gap-8">
                            {t('energy.cards').map((card, index) => (
                                <div key={index} className="bg-accent-powder-green p-6 rounded-lg">
                                    <h3 className="font-poppins font-semibold text-h3 mb-4 text-primary-red">{card.title}</h3>
                                    <p className="font-poppins font-normal text-body text-gray-700">
                                        {card.body}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Continuous Improvement */}
            <section className="py-20 bg-background-cool-blue">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="font-poppins font-light text-h2 mb-8 text-primary-dark-red">
                            {t('improvement.title')}
                        </h2>
                        {t('improvement.paragraphs').map((paragraph, index) => (
                            <p key={index} className="font-poppins font-normal text-body mb-6 last:mb-0">
                                {paragraph}
                            </p>
                        ))}
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-20 bg-primary-dark-red text-white">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="font-poppins font-light text-h2 mb-6">
                        {t('cta.title')}
                    </h2>
                    <p className="font-poppins font-normal text-body max-w-2xl mx-auto mb-10">
                        {t('cta.body')}
                    </p>
                    <a
                        href={`mailto:${g('contact.email')}`}
                        className="inline-block px-8 py-4 bg-primary-red text-white hover:bg-pink-600 transition-colors duration-300 font-poppins font-semibold text-h3 rounded-lg"
                    >
                        {t('cta.buttonText')}
                    </a>
                </div>
            </section>
        </Layout>
    );
};

export default EnvironmentalPolicyPage;
